// Frontend API service for sync operations
import { getAuthHeaders, handleAuthError } from './authUtils';

// Treat REACT_APP_API_URL as the origin (scheme+host+port). Always prefix paths with /api
const API_ORIGIN = process.env.REACT_APP_API_URL || 'http://localhost:5001';
const API_BASE_URL = `${API_ORIGIN}/api`;

class SyncApiService {
  normalizeEntityIds(payload = {}) {
    const candidates = [payload.entityIds, payload.ids];
    for (const candidate of candidates) {
      const values = Array.isArray(candidate) ? candidate : (candidate ? [candidate] : []);
      const normalized = values
        .map((value) => String(value || '').trim())
        .filter(Boolean);
      if (normalized.length > 0) return normalized;
    }
    return [];
  }

  // Start a sync operation for a specific service
  async startSync(serviceName, payload = {}) {
    try {
      // Get current workspace ID from localStorage
      const selectedWorkspace = localStorage.getItem('selectedWorkspace');
      const currentWorkspace = selectedWorkspace ? JSON.parse(selectedWorkspace) : null;
      const workspaceId = currentWorkspace?.id;
      
      
      // Create a clean, minimal payload for n8n
      const cleanPayload = {
        tenantId: workspaceId || payload.tenantId || null,
        entityIds: this.normalizeEntityIds(payload), // Support both entityIds and ids fields
        timestamp: new Date().toISOString()
      };
      
      
      // Debug: check what headers will be sent
      const headers = getAuthHeaders();
      
      // Debug: show the exact URL being called
      const fullUrl = `${API_BASE_URL}/sync/${serviceName}/start`;
      
      const response = await fetch(fullUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify(cleanPayload),
      });
      
      if (!response.ok) {
        // Check if it's a 401 error and handle it globally
        if (response.status === 401) {
          const error = { response: { status: 401 } };
          if (handleAuthError(error)) {
            throw new Error('Session expired - redirected to login');
          }
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      // SyncApi startSync - error details
      throw error;
    }
  }

  // Get current sync status for a service
  async getSyncStatus(serviceName) {
    try {
      const response = await fetch(`${API_BASE_URL}/sync/${serviceName}/status`, {
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        // Check if it's a 401 error and handle it globally
        if (response.status === 401) {
          const error = { response: { status: 401 } };
          if (handleAuthError(error)) {
            throw new Error('Session expired - redirected to login');
          }
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  // Stop a running sync operation
  async stopSync(serviceName) {
    try {
      const response = await fetch(`${API_BASE_URL}/sync/${serviceName}/stop`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        // Check if it's a 401 error and handle it globally
        if (response.status === 401) {
          const error = { response: { status: 401 } };
          if (handleAuthError(error)) {
            throw new Error('Session expired - redirected to login');
          }
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      throw error;
    }
  }
}

export const syncApi = new SyncApiService();
